import { useState } from 'react'
import { Button } from './ui'
import { useToast } from '@/context/ToastContext'
import { useAuth } from '@/context/AuthContext'
import { api } from '@/api'
import type { RefinePromptRequest } from '@/api/types'

/** 改写场景。真源在后端 `aigc._REFINE_SYSTEM`，这里只是把它的键名带过来 */
type RefineKind = NonNullable<RefinePromptRequest['kind']>

/**
 * 「AI 重写这一份提示词」按钮 —— **全站唯一一份实现**。
 *
 * 2026-10-08 用户口径：「可以修改提示词，还有提示词生成可以多一个 AI 按钮重置」。
 * 落点是提交前的报价确认弹窗：逐张提示词旁边各一颗。
 *
 * 与 `AiFillButton` 是亲兄弟（同一族四条约定），差别只有一处：
 * `AiFillButton` 把返回的**结构化字段**写回表单，这一颗把返回的**一整段正文**
 * 交回给调用方。所以它不做字段白名单，只做「空结果报错」。
 *
 * ## 四条不可省的约定（与 `/form/fill` 同族）
 *
 * ① **它不是生成任务**。不建 job、不进任务中心、右栏不会多一张卡 ——
 *    它改的是**输入**（将要发出去的那段提示词）。但它真花钱
 *    （后端按条 ¥0.10 并落用量账本），所以失败要如实报、成功要报清花了多少。
 * ①' **拿到响应就刷余额**。后端 `_bill_text_call` 在解析之前就扣过积分了，
 *    所以刷新必须放在 `await` 之后、成功分支**之外** —— 放进成功分支的话，
 *    「花了钱 + 提示失败 + 顶栏一动不动」，用户会以为既没花钱也没写成，然后再点一次。
 *    额度数用后端回的 `coins`（积分口径，与顶栏同源），**不写 `cost × 10`**。
 * ② **空结果必须报错**。「花了钱、提示成功、文字一个字没变」是最坏的失败形态 ——
 *    后端已经保证过一次（`_strip_fence` 之后可能为空），这里同样不放过。
 * ③ **覆盖前留快照，成功给「撤销」**。`prompt` 就是调用前的那一份
 *    （闭包里捕获的 prop，天然是快照），AI 重写覆盖掉用户手改过的内容后
 *    必须能一键拿回来 —— 不给撤销的覆盖，用户下次就不敢点了。
 *
 * ## 为什么「禁用」而不是「隐藏」
 *
 * 前置条件不满足时（比如没填产品名）按钮**留在原位、变灰**，原因写进 `title`。
 * 藏起来的话用户永远不知道这里有捷径；而 AI 编错一次的成本，
 * 远高于他先补一个产品名。
 */
export function AiPromptButton({
  kind,
  brief,
  prompt,
  sourcePage,
  onRefined,
  blocked,
  label = 'AI 重写',
  busyLabel = 'AI 正在写…',
}: {
  /** 改写场景。合法值 = 后端 `aigc._REFINE_SYSTEM` 的键（**那里是唯一真源**） */
  kind: RefineKind
  /** 场景上下文。**由调用方用本页真源拼**（见 `lib/detailPrompts.ts` 的 `buildShotRefineBrief`） */
  brief: string
  /** 当前这一份（AI 在它基础上改写）。**同时是「撤销」要回到的那一份** */
  prompt: string
  /** 出处，只用于用量账本 */
  sourcePage?: string
  /** 拿到新正文后写回（通常是弹窗里的草稿 state） */
  onRefined: (text: string) => void
  /** 不让点的原因。有值 = 禁用，值会出现在 hover 提示里 */
  blocked?: string
  label?: string
  busyLabel?: string
}) {
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  const { refresh: refreshBalance } = useAuth()

  const run = async () => {
    if (busy || blocked) return
    setBusy(true)
    try {
      const res = await api.refinePrompt({ kind, brief, prompt, sourcePage })
      /* 拿到响应 = 后端**已经收过费**（`_bill_text_call` 记在解析之前）。
         所以刷新放在这里，不能放进下面的成功分支。 */
      void refreshBalance()
      const text = res.prompt.trim()
      if (!text) throw new Error('模型没有返回任何内容，这一份仍是原来那段')
      onRefined(text)
      const coinNote = res.coins > 0 ? ` · 扣 ${res.coins} 积分` : ''
      toast.toast(
        `AI 已重写这一份（${res.model} · ¥${res.cost.toFixed(2)}${coinNote} · ` +
          `${(res.elapsedMs / 1000).toFixed(1)}s）。可以直接改，不满意就点「撤销」。`,
        'success',
        { label: '撤销', onClick: () => onRefined(prompt) },
      )
    } catch (e) {
      // 如实报错，且把后端/上游的原话带出来：Key 失效、限流、空白正文
      // 三种原因的处理方式完全不同，吞成「失败了」等于让人去猜。
      toast.error(`AI 重写失败：${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      icon="sparkles"
      loading={busy}
      /* 禁用而不是藏起来：让「这里能一键重写」一直看得见（见组件头注） */
      disabled={!!blocked}
      title={blocked}
      onClick={run}
    >
      {busy ? busyLabel : label}
    </Button>
  )
}
